// src/components/NeuralBackground.tsx
import React, { useEffect, useRef } from 'react';

interface NeuralNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  activation: number;
  id: number;
}

interface NeuralConnection {
  from: number;
  to: number;
  strength: number;
  active: boolean;
}

const NeuralBackground: React.FC = () => {
  const backgroundRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const nodesRef = useRef<NeuralNode[]>([]);
  const connectionsRef = useRef<NeuralConnection[]>([]);

  useEffect(() => {
    const background = backgroundRef.current;
    if (!background) return;

    // Clear any existing content
    background.innerHTML = '';

    // Configuration
    const nodeCount = 50;
    const connectionCount = 80;
    const maxConnectionDistance = 200;

    // Initialize nodes
    nodesRef.current = Array.from({ length: nodeCount }, (_, i) => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      activation: Math.random(),
      id: i
    }));

    // Create DOM elements for nodes
    const nodeElements = nodesRef.current.map(() => {
      const node = document.createElement('div');
      node.className = 'neural-node';
      background.appendChild(node);
      return node;
    });

    // Initialize connections
    connectionsRef.current = [];
    for (let i = 0; i < connectionCount; i++) {
      const from = Math.floor(Math.random() * nodeCount);
      let to = Math.floor(Math.random() * nodeCount);
      while (to === from) {
        to = Math.floor(Math.random() * nodeCount);
      }
      
      connectionsRef.current.push({
        from,
        to,
        strength: Math.random(),
        active: false
      });
    }

    // Create DOM elements for connections
    const connectionElements = connectionsRef.current.map(() => {
      const connection = document.createElement('div');
      connection.className = 'neural-connection';
      background.appendChild(connection);
      return connection;
    });

    // Animation loop
    const animate = () => {
      const time = Date.now() * 0.001;
      
      // Update nodes
      nodesRef.current.forEach((node, i) => {
        // Update position
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off edges
        if (node.x <= 0 || node.x >= window.innerWidth) {
          node.vx *= -1;
          node.x = Math.max(0, Math.min(window.innerWidth, node.x));
        }
        if (node.y <= 0 || node.y >= window.innerHeight) {
          node.vy *= -1;
          node.y = Math.max(0, Math.min(window.innerHeight, node.y));
        }

        // Update activation with sine wave for organic pulsing
        node.activation = 0.3 + 0.7 * Math.sin(time + i * 0.2);

        // Update DOM element
        const element = nodeElements[i];
        if (element) {
          element.style.left = node.x + 'px';
          element.style.top = node.y + 'px';
          element.style.opacity = (0.3 + node.activation * 0.7).toString();
          
          const size = 2 + node.activation * 4;
          element.style.width = size + 'px';
          element.style.height = size + 'px';
          
          // Glow effect for highly activated nodes
          if (node.activation > 0.8) {
            element.style.boxShadow = `0 0 ${size * 2}px rgba(99, 102, 241, ${node.activation})`;
          } else {
            element.style.boxShadow = 'none';
          }
        }
      });

      // Update connections
      connectionsRef.current.forEach((connection, i) => {
        const fromNode = nodesRef.current[connection.from];
        const toNode = nodesRef.current[connection.to];
        
        if (!fromNode || !toNode) return;

        // Calculate distance
        const dx = toNode.x - fromNode.x;
        const dy = toNode.y - fromNode.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Only show connection if nodes are close enough
        const element = connectionElements[i];
        if (element) {
          if (distance < maxConnectionDistance) {
            // Calculate opacity based on distance and activation
            const baseOpacity = Math.max(0, 1 - distance / maxConnectionDistance);
            const activationBoost = (fromNode.activation + toNode.activation) / 2;
            const opacity = baseOpacity * activationBoost * 0.4;

            // Position and rotate the connection line
            element.style.left = fromNode.x + 'px';
            element.style.top = fromNode.y + 'px';
            element.style.width = distance + 'px';
            element.style.opacity = opacity.toString();
            
            const angle = Math.atan2(dy, dx) * 180 / Math.PI;
            element.style.transform = `rotate(${angle}deg)`;
            element.style.transformOrigin = '0 50%';
            
            // Connection is active if both nodes are highly activated
            connection.active = fromNode.activation > 0.7 && toNode.activation > 0.7;
            
            if (connection.active) {
              element.style.background = `linear-gradient(90deg, 
                rgba(99, 102, 241, 0), 
                rgba(99, 102, 241, ${opacity * 2}), 
                rgba(99, 102, 241, 0))`;
            } else {
              element.style.background = `linear-gradient(90deg, 
                rgba(99, 102, 241, 0), 
                rgba(99, 102, 241, ${opacity}), 
                rgba(99, 102, 241, 0))`;
            }
          } else {
            element.style.opacity = '0';
          }
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animate();

    // Handle resize
    const handleResize = () => {
      // Update node boundaries
      nodesRef.current.forEach(node => {
        node.x = Math.min(node.x, window.innerWidth);
        node.y = Math.min(node.y, window.innerHeight);
      });
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Add special effects on user interaction
  useEffect(() => {
    const background = backgroundRef.current;
    if (!background) return;

    const handleMouseMove = (e: MouseEvent) => {
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      const interactionRadius = 150;

      // Activate nearby nodes
      nodesRef.current.forEach(node => {
        const dx = mouseX - node.x;
        const dy = mouseY - node.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < interactionRadius) {
          const influence = 1 - distance / interactionRadius;
          node.activation = Math.min(1, node.activation + influence * 0.3);
          
          // Add slight attraction to mouse
          const force = influence * 0.01;
          node.vx += dx * force * 0.001;
          node.vy += dy * force * 0.001;
        }
      });
    };

    const handleClick = (e: MouseEvent) => {
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      const activationRadius = 200;

      // Create activation wave
      nodesRef.current.forEach(node => {
        const dx = mouseX - node.x;
        const dy = mouseY - node.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < activationRadius) {
          const influence = 1 - distance / activationRadius;
          setTimeout(() => {
            node.activation = 1;
          }, distance * 2); // Delay based on distance for wave effect
        }
      });
    };

    background.addEventListener('mousemove', handleMouseMove);
    background.addEventListener('click', handleClick);

    return () => {
      background.removeEventListener('mousemove', handleMouseMove);
      background.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <div 
      ref={backgroundRef}
      className="neural-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: 0.1
      }}
    />
  );
};

export default NeuralBackground;